import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaArrowRight, FaCheck, FaLock, FaBook, FaListUl, FaCheckCircle } from 'react-icons/fa';
import { getLessonById, clearLesson, markLessonCompleted } from '../../features/lessons/lessonSlice';
import { getCourseById } from '../../features/courses/courseSlice';
import { getLessonsByCourse } from '../../features/lessons/lessonSlice';
import { getCourseProgress, updateLessonProgress } from '../../features/progress/progressSlice';

const LessonDetail = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { lesson, isLoading: lessonLoading, isSuccess: lessonSuccess } = useSelector((state) => state.lessons);
  const { course } = useSelector((state) => state.courses);
  const { lessons } = useSelector((state) => state.lessons);
  const { progress, isLoading: progressLoading } = useSelector((state) => state.progress);
  
  const [showSidebar, setShowSidebar] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isCompletingLesson, setIsCompletingLesson] = useState(false);
  
  useEffect(() => {
    dispatch(getLessonById(lessonId));
    
    return () => {
      dispatch(clearLesson());
    };
  }, [dispatch, lessonId]);
  
  useEffect(() => {
    if (lesson && lesson.courseId) {
      dispatch(getCourseById(lesson.courseId));
      dispatch(getLessonsByCourse(lesson.courseId));
      dispatch(getCourseProgress(lesson.courseId));
    }
  }, [dispatch, lesson]);
  
  useEffect(() => {
    if (progress && progress.completedLessons && lessonId) {
      setIsCompleted(progress.completedLessons.includes(lessonId));
    }
  }, [progress, lessonId]);
  
  // Get current lesson index and adjacent lessons
  const currentIndex = lessons.findIndex(l => l._id === lessonId);
  const prevLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null;
  
  // Check if a lesson is accessible
  const isLessonAccessible = (index) => {
    // First lesson is always accessible
    if (index === 0) return true;
    
    // If previous lesson is completed, this lesson is accessible
    const previousLesson = lessons[index - 1];
    if (previousLesson && progress?.completedLessons?.includes(previousLesson._id)) return true;
    
    return false;
  };
  
  // Handle mark as completed
  const handleMarkCompleted = async () => {
    if (isCompleted) return;
    
    setIsCompletingLesson(true);
    try {
      // First update locally for immediate UI feedback
      dispatch(updateLessonProgress({ lessonId, completed: true }));
      setIsCompleted(true);
      
      // Then send to server
      await dispatch(markLessonCompleted(lessonId)).unwrap();
      toast.success('Lesson marked as completed!');
      
      // If there's a next lesson and it's now accessible, offer to navigate there
      if (nextLesson) {
        // Small delay to allow the progress state to update
        setTimeout(() => {
          navigate(`/dashboard/lessons/${nextLesson._id}`);
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to mark lesson as completed');
      // Revert the local update
      dispatch(updateLessonProgress({ lessonId, completed: false }));
      setIsCompleted(false);
    } finally {
      setIsCompletingLesson(false);
    }
  };
  
  // Loading state
  if (lessonLoading || !lesson || !course) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return (
    <div className="flex h-full">
      {/* Lesson sidebar (mobile: hidden by default, desktop: always visible) */}
      <div 
        className={`fixed inset-y-0 left-0 z-30 w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 ${showSidebar ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Course info */}
        <div className="p-4 border-b">
          <Link 
            to={`/dashboard/courses/${course._id}`}
            className="text-primary hover:underline flex items-center text-sm mb-2"
          >
            <FaArrowLeft className="mr-1" />
            Back to Course
          </Link>
          <h2 className="font-bold truncate">{course.title}</h2>
          <div className="flex items-center text-sm text-gray-500">
            <FaBook className="mr-1" />
            <span>{lessons.length} Lessons</span>
          </div>
        </div>
        
        {/* Lessons list */}
        <div className="overflow-y-auto h-full pb-20">
          <div className="p-4">
            <h3 className="font-medium mb-3 flex items-center">
              <FaListUl className="mr-2" />
              Course Content
            </h3>
            
            <div className="space-y-2">
              {lessons.map((l, index) => {
                const isActive = l._id === lessonId;
                const isLessonCompleted = progress?.completedLessons?.includes(l._id);
                const canAccess = isLessonAccessible(index);
                
                return (
                  <div 
                    key={l._id} 
                    className={`p-2 rounded text-sm ${isActive ? 'bg-primary text-white' : isLessonCompleted ? 'bg-green-50 text-green-800' : 'hover:bg-gray-100'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                          {isLessonCompleted ? (
                            <FaCheckCircle className={isActive ? 'text-white' : 'text-green-600'} />
                          ) : (
                            <span className={`text-xs ${isActive ? 'bg-white text-primary' : 'bg-gray-200 text-gray-600'} w-5 h-5 rounded-full flex items-center justify-center`}>
                              {index + 1}
                            </span>
                          )}
                        </div>
                        
                        {canAccess ? (
                          <Link 
                            to={`/dashboard/lessons/${l._id}`}
                            className={`truncate ${isActive ? 'font-medium' : ''}`}
                          >
                            {l.title}
                          </Link>
                        ) : (
                          <div className="flex items-center text-gray-400">
                            <span className="truncate">{l.title}</span>
                            <FaLock className="ml-1 text-xs" />
                          </div>
                        )}
                      </div>
                      
                      {l.duration && (
                        <span className={`text-xs ${isActive ? 'text-white text-opacity-80' : 'text-gray-500'}`}>
                          {l.duration} min
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 overflow-auto">
        {/* Mobile header with toggle */}
        <div className="lg:hidden bg-white border-b p-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="flex items-center text-sm text-primary"
            >
              <FaListUl className="mr-1" />
              Course Content
            </button>
            
            <Link 
              to={`/dashboard/courses/${course._id}`}
              className="text-primary text-sm"
            >
              Back to Course
            </Link>
          </div>
        </div>
        
        {/* Lesson content */}
        <div className="p-6">
          <div className="bg-white container-rounded-lg shadow overflow-hidden mb-6">
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">{lesson.title}</h1>
              
              {/* Video or main content */}
              {lesson.videoUrl ? (
                <div className="aspect-w-16 aspect-h-9 mb-6">
                  <iframe 
                    src={lesson.videoUrl} 
                    title={lesson.title}
                    className="w-full h-full container-rounded"
                    allowFullScreen
                  ></iframe>
                </div>
              ) : lesson.imageUrl ? (
                <div className="mb-6">
                  <img 
                    src={lesson.imageUrl} 
                    alt={lesson.title}
                    className="w-full container-rounded"
                  />
                </div>
              ) : (
                <div className="h-64 bg-gray-100 flex items-center justify-center mb-6 container-rounded">
                  <p className="text-gray-500">No media content available for this lesson</p>
                </div>
              )}
              
              {/* Lesson content */}
              <div className="prose max-w-none">
                <div dangerouslySetInnerHTML={{ __html: lesson.content || '<p>No content available for this lesson.</p>' }} />
              </div>
            </div>
          </div>
          
          {/* Resources section */}
          {lesson.resources && lesson.resources.length > 0 && (
            <div className="bg-white container-rounded-lg shadow overflow-hidden mb-6">
              <div className="p-6">
                <h2 className="text-lg font-bold mb-4">Resources</h2>
                <ul className="space-y-2">
                  {lesson.resources.map((resource, index) => (
                    <li key={index}>
                      <a 
                        href={resource.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center"
                      >
                        <FaBook className="mr-2" />
                        {resource.title || 'Resource'}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* Navigation and completion */}
          <div className="bg-white container-rounded-lg shadow overflow-hidden">
            <div className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <div className="flex space-x-3 mb-4 sm:mb-0">
                  {prevLesson ? (
                    <Link 
                      to={`/dashboard/lessons/${prevLesson._id}`}
                      className="btn-secondary flex items-center"
                    >
                      <FaArrowLeft className="mr-2" />
                      Previous Lesson
                    </Link>
                  ) : (
                    <button className="btn-secondary opacity-50 cursor-not-allowed flex items-center" disabled>
                      <FaArrowLeft className="mr-2" />
                      Previous Lesson
                    </button>
                  )}
                  
                  {nextLesson ? (
                    isLessonAccessible(currentIndex + 1) ? (
                      <Link 
                        to={`/dashboard/lessons/${nextLesson._id}`}
                        className="btn-secondary flex items-center"
                      >
                        Next Lesson
                        <FaArrowRight className="ml-2" />
                      </Link>
                    ) : (
                      <button className="btn-secondary opacity-50 cursor-not-allowed flex items-center" disabled>
                        Next Lesson
                        <FaLock className="ml-2" />
                      </button>
                    )
                  ) : (
                    <Link 
                      to={`/dashboard/courses/${course._id}`}
                      className="btn-secondary flex items-center"
                    >
                      Back to Course
                      <FaArrowRight className="ml-2" />
                    </Link>
                  )}
                </div>
                
                {isCompleted ? (
                  <div className="flex items-center text-green-600 bg-green-50 px-4 py-2 container-rounded">
                    <FaCheckCircle className="mr-2" />
                    Completed
                  </div>
                ) : (
                  <button
                    onClick={handleMarkCompleted}
                    className="btn-primary flex items-center"
                    disabled={isCompletingLesson}
                  >
                    {isCompletingLesson ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        Marking as Completed...
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-2" />
                        Mark as Completed
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Backdrop for mobile sidebar */}
      {showSidebar && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setShowSidebar(false)}
        ></div>
      )}
    </div>
  );
};

export default LessonDetail;